import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { locations } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectOptions = [
  "Full home renovation",
  "Kitchen remodel",
  "Bathroom remodel",
  "Addition / Extension",
  "Brownstone / Co-op",
  "New build",
  "Just exploring",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", location: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", project_type: "", location: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 pt-16 pb-12">
        <p className="section-tag mb-4">// LET'S TALK</p>
        <h1 className="font-display text-[72px] sm:text-[112px] lg:text-[160px] uppercase leading-[0.88]">
          Start your<br /><span className="text-[var(--terracotta)]">project.</span>
        </h1>
      </section>

      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 pb-20">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {/* Form */}
          <div className="col-span-12 lg:col-span-8 bento-card p-6 lg:p-10">
            {success ? (
              <div className="text-center py-12">
                <CheckCircle2 size={56} className="mx-auto text-[var(--terracotta)] mb-4" strokeWidth={1.4} />
                <p className="section-tag mb-3">// SUBMISSION RECEIVED</p>
                <h3 className="font-display text-[42px] uppercase">Thanks for reaching out</h3>
                <p className="mt-4 text-[14px] text-[var(--slate-700)] max-w-md mx-auto leading-7">
                  Your inquiry has been routed to <span className="font-semibold">info@edge-developerllc.com</span>. A member of the Edge Developer team will be in touch within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <p className="section-tag mb-3">// PROJECT INQUIRY</p>
                <h3 className="font-display text-[34px] sm:text-[44px] uppercase leading-[0.95]">Tell us about it.</h3>
                <p className="mt-2 text-[13.5px] text-[var(--slate-700)] max-w-xl">A real person reads every submission. Average reply time: under 24 hours.</p>

                <div className="grid sm:grid-cols-2 gap-4 mt-7">
                  <Field label="Full Name" name="name" value={form.name} onChange={handleChange} required />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
                  <Field label="Phone (optional)" name="phone" value={form.phone} onChange={handleChange} />
                  <Field label="Project Location" name="location" value={form.location} onChange={handleChange} placeholder="e.g. Manhattan, NY" />
                </div>

                <div className="mt-4">
                  <label className="block font-mono-sans text-[10.5px] font-semibold mb-1.5">PROJECT TYPE</label>
                  <select name="project_type" value={form.project_type} onChange={handleChange}
                    className="w-full bg-transparent border border-[var(--slate-300)] px-3 py-2.5 text-[13.5px] focus:outline-none focus:border-[var(--terracotta)] transition-colors">
                    <option value="">Select...</option>
                    {projectOptions.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>

                <div className="mt-4">
                  <label className="block font-mono-sans text-[10.5px] font-semibold mb-1.5">TELL US ABOUT YOUR PROJECT</label>
                  <textarea name="message" value={form.message} onChange={handleChange} required rows={5}
                    placeholder="Scope, timeline, budget range, anything we should know..."
                    className="w-full bg-transparent border border-[var(--slate-300)] px-3 py-2.5 text-[13.5px] focus:outline-none focus:border-[var(--terracotta)] transition-colors resize-y" />
                </div>

                {error && (
                  <div className="mt-4 text-[13px] text-[var(--terracotta-dark)] bg-[#f6e1de] border border-[var(--terracotta-light)] px-3 py-2">{error}</div>
                )}

                <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                  <p className="font-mono-sans text-[10.5px] text-[var(--slate-500)] tracking-wider">DELIVERED TO INFO@EDGE-DEVELOPERLLC.COM</p>
                  <button type="submit" disabled={submitting} className="btn-terracotta disabled:opacity-60 disabled:cursor-not-allowed">
                    {submitting ? "Sending..." : (<>Send Inquiry <Send size={14} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Side: direct contact */}
          <aside className="col-span-12 lg:col-span-4 space-y-4">
            <div className="bento-card dark p-6">
              <p className="section-tag !text-[var(--terracotta-light)] mb-3">// DIRECT</p>
              <h4 className="font-display text-[24px] uppercase">Talk to a human</h4>
              <ul className="mt-4 space-y-3">
                <li className="flex items-center gap-3 text-[13px]"><Phone size={14} className="text-[var(--terracotta-light)]" /> <a href="tel:+15073518787" className="hover:text-[var(--terracotta-light)]">(507) 351-8787</a></li>
                <li className="flex items-center gap-3 text-[13px]"><Mail size={14} className="text-[var(--terracotta-light)]" /> <a href="mailto:info@edge-developerllc.com" className="hover:text-[var(--terracotta-light)] break-all">info@edge-developerllc.com</a></li>
              </ul>
            </div>
            <div className="bento-card terracotta p-6">
              <p className="font-mono-sans text-[10px] mb-3 opacity-80">// HOURS</p>
              <h4 className="font-display text-[24px] uppercase">Mon – Fri</h4>
              <p className="text-[13.5px] mt-1">8:00 AM – 6:00 PM EST</p>
              <p className="text-[12px] opacity-90 mt-3">Weekends: emergencies only.</p>
            </div>
            <div className="bento-card p-6">
              <p className="section-tag mb-3">// OFFICES</p>
              <ul className="space-y-3">
                {locations.map((l) => (
                  <li key={l.slug} className="flex items-start gap-2 text-[12.5px] text-[var(--slate-700)]">
                    <MapPin size={13} className="text-[var(--terracotta)] mt-0.5 shrink-0" />
                    <div>
                      <div className="font-mono-sans text-[10px] text-[var(--slate-900)] font-semibold">{l.name.toUpperCase()}</div>
                      <div>{l.address}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block font-mono-sans text-[10.5px] font-semibold mb-1.5">
        {label.toUpperCase()}{required && <span className="text-[var(--terracotta)]"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full bg-transparent border border-[var(--slate-300)] px-3 py-2.5 text-[13.5px] focus:outline-none focus:border-[var(--terracotta)] transition-colors"
      />
    </div>
  );
}
